import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { nexusPluginsFixture, probeFixture } from './fixture';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-plugin-config.md
 * Endpoints: /v1/admin/plugins (GET/PATCH/PUT/POST)
 */

/** GET /v1/admin/plugins → { plugins: [...] } oder { items: [...] } */
export function useNexusPlugins() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-plugins', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return nexusPluginsFixture();
      }
      const res = await nexusFetch('/v1/admin/plugins');
      // Toleriert mehrere Wrapper-Variationen
      const list = res?.plugins ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { plugins: list, raw: res };
    },
    staleTime: 30_000,
  });
}

/** PATCH /v1/admin/plugins/:id — `{ enabled?, is_default?, config? }` */
export function useUpdateNexusPlugin() {
  return useApiMutation({
    live: (_fetch, { id, patch }) =>
      nexusFetch(`/v1/admin/plugins/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    fixture: ({ id, patch }) => {
      // Mock: trivial — fixture-Mode hat keinen Persist-Layer für Nexus
      return { ok: true, id, patch };
    },
    invalidate: ['nexus-plugins'],
    successMessage: (_, { id }) => `Plugin ${id} aktualisiert.`,
  });
}

/** PUT /v1/admin/plugins/:id/credentials — `{ tenant_id?, config }` */
export function useSetNexusPluginCredentials() {
  return useApiMutation({
    live: (_fetch, { id, tenant_id, config }) =>
      nexusFetch(`/v1/admin/plugins/${encodeURIComponent(id)}/credentials`, {
        method: 'PUT',
        body: { tenant_id: tenant_id ?? null, config },
      }),
    fixture: ({ id }) => ({ ok: true, id }),
    invalidate: ['nexus-plugins'],
    successMessage: (_, { id, tenant_id }) =>
      tenant_id
        ? `Credentials für ${id} / Tenant ${tenant_id} gespeichert.`
        : `Globaler Default für ${id} gespeichert.`,
  });
}

/** POST /v1/admin/plugins/:id/probe — Smoke-Call gegen den Provider */
export function useProbeNexusPlugin() {
  const qc = useQueryClient();
  return useApiMutation({
    live: (_fetch, { id, tenant_id }) =>
      nexusFetch(`/v1/admin/plugins/${encodeURIComponent(id)}/probe`, {
        method: 'POST',
        body: { tenant_id: tenant_id ?? null },
      }),
    fixture: ({ id }) => probeFixture(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nexus-plugins'] }),
    // Kein auto-toast — UI zeigt das Probe-Resultat inline
  });
}
